// coogo terminology resolver — DETERMINISTIC, workerd-safe leaf.
//
// Operator direction (2026-07-24): coogo defines its terminology, holds it
// internally, and uses it. An LLM may be wrong; RAG may not. So nothing here
// infers, ranks by model, or mints vocabulary: this module only answers
// "which DEFINED terms look like what the user said?" against the compiled
// index (ontology/terminology-index.v1.json).
//
// The intended use is the ambiguity feedback loop: when a request does not
// resolve to exactly one defined term, show the candidates and let the USER
// choose. The user's selection is the ground truth — which is what makes the
// retrieval-side false-positive rate zero by construction, because no term
// that was never defined can be selected.
//
// Constraints this file must keep:
//   - no fs, no network, no LLM (node:fs in a Worker has crashed deploys here)
//   - pure + deterministic: same query → same ranking, always
//   - it never invents: every returned id exists in the index

import indexJson from '../ontology/terminology-index.v1.json' with { type: 'json' }

export const TERMINOLOGY_INDEX_SCHEMA_VERSION = 'cogo.terminology-index.v1'

export type TerminologyKind =
  | 'element-type'
  | 'action'
  | 'event'
  | 'flow-trigger'
  | 'flow-step'
  | 'surface-term'
  /**
   * A term an APPLICATION contributed to its own scope (Task 3), via a file
   * conforming to ontology/application-terminology.schema.json. Kept
   * distinct from 'surface-term' — which is reserved for the platform's own
   * knowledge-units layer — so a platform-only invariant over 'surface-term'
   * entries never has to reason about per-application vocabulary too.
   */
  | 'application-term'
  /**
   * An ActionFlow step parameter name, derived from `cogo:declaresParam` in
   * cogo-kg-browser/fixtures/actionflow-executors-v1.ttl (coverage fill,
   * 2026-08-14) — the runtime's OWN vocabulary (resultTarget, route, method,
   * mode, payload, eventName, …), one entry per distinct name across every
   * executor. Kept distinct from 'action' — a param name and an action name
   * can collide (e.g. "delay" is both) and must resolve as two separate,
   * unambiguous entries, not one merged one.
   */
  | 'param-key'
  /**
   * One of the canonical-9 `#{prefix.path}` expression prefixes, derived
   * from `cogo:prefixName` in cogo-kg-browser/fixtures/expression-prefix-v1.
   * ttl (coverage fill, 2026-08-14) — local/event/props/item/_results/
   * computed/appData/env/route.
   */
  | 'expression-prefix'

/**
 * How this term's canonical symbol stands against a CONNECTED MODEL's
 * default output, when that alignment was assessed (terminology-alignment
 * Task 2, 2026-08-14). Ported from an application-terminology store term's
 * own `alignment` block (application-terminology.schema.json) — this is
 * NOT the full block: `generate-terminology-index.mjs`'s `entry()` carries
 * exactly `decision` / `canonicalSymbol` / `priorSymbols` / `rationale`
 * from the store and deliberately DROPS that block's `usage` (a literal
 * excerpt of real source text — must never reach the index or a prompt)
 * and `measuredIn` (run metadata — model id, run counts — not vocabulary).
 * See `scripts/alignment-excluded-fields.mjs` for the authoritative
 * exclusion list and the reason for each excluded key.
 */
export interface TerminologyAlignment {
  /**
   * adopt = the symbol becomes canonical (nothing carried here — an
   * adopted symbol renders no difference). teach = canonical stays and the
   * model's prior symbol is mapped onto it. diverge = deliberate mismatch,
   * same injection cost as teach plus a recorded reason for paying it.
   */
  decision: 'adopt' | 'teach' | 'diverge'
  /** The symbol coogo honours. */
  canonicalSymbol: string
  /** What the connected model produced for the same concept. */
  priorSymbols: string[]
  rationale: string
}

export interface TerminologyEntry {
  id: string
  kind: TerminologyKind
  /**
   * Vocabulary scope this entry belongs to — "platform" for coogo's own
   * ontology, distinct scopes for per-application vocabulary once that
   * exists. Every entry has one; there is no unscoped vocabulary.
   */
  scope: string
  source: string
  category?: string
  description?: string
  /** Surface forms a user might actually say (tags, Korean source terms). */
  terms?: string[]
  /** Canonical cogo: ontology identifiers this entry binds to. */
  ontologyRefs?: string[]
  /**
   * Present only for terms whose store contributed an `alignment` block.
   * See {@link TerminologyAlignment} for exactly what is (and is not)
   * carried.
   */
  alignment?: TerminologyAlignment
}

interface TerminologyIndexFile {
  schemaVersion: string
  total: number
  scopes: string[]
  counts: Record<string, number>
  scopeCounts: Record<string, Record<string, number>>
  entries: TerminologyEntry[]
}

const INDEX = indexJson as unknown as TerminologyIndexFile

/**
 * All entries in the compiled index, ACROSS EVERY SCOPE — platform vocabulary
 * unioned with every application's own vocabulary. This is a scope-MIXED
 * pool: it is the right shape for cross-scope invariants and tooling (e.g.
 * "does every entry have a source"), but the WRONG shape for building a
 * resolution candidate pool for one caller — using it unfiltered for that
 * purpose reintroduces the cross-application vocabulary leak this index
 * exists to prevent. Pass `scope` to get one scope's entries only (e.g.
 * `'platform'`); omit it to get today's full mixed pool, unchanged.
 */
export function terminologyEntries(scope?: string): readonly TerminologyEntry[] {
  if (scope === undefined) return INDEX.entries
  return INDEX.entries.filter((entry) => entry.scope === scope)
}

/** Flat kind → n counts, PLATFORM-SCOPE ONLY. Never includes any application's entries. */
export function terminologyCounts(): Readonly<Record<string, number>> {
  return INDEX.counts
}

/** Every scope present in the index (platform AND every application scope), sorted. */
export function terminologyScopes(): readonly string[] {
  return INDEX.scopes
}

/**
 * scope → kind → n, covering EVERY scope in the index (platform and every
 * application). NOT a breakdown of {@link terminologyCounts} — that accessor
 * is platform-only, this one spans every scope; `scopeCounts.platform`
 * equals `terminologyCounts()`, but the other keys have no counterpart there.
 */
export function terminologyScopeCounts(): Readonly<
  Record<string, Readonly<Record<string, number>>>
> {
  return INDEX.scopeCounts
}

// ─── matching primitives (deterministic) ───────────────────────────────────

/** NFKC + lowercase; keeps CJK intact so Korean surface terms compare cleanly. */
function fold(value: string): string {
  return value.normalize('NFKC').trim().toLowerCase()
}

/** Split an identifier or phrase into comparable word parts. */
function parts(value: string): string[] {
  return fold(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
}

/** Character bigrams — the similarity backbone; works for CJK and typos alike. */
function bigrams(value: string): Set<string> {
  const f = fold(value).replace(/\s+/g, '')
  const out = new Set<string>()
  if (f.length === 1) out.add(f)
  for (let i = 0; i + 1 < f.length; i++) out.add(f.slice(i, i + 2))
  return out
}

function diceCoefficient(a: string, b: string): number {
  const A = bigrams(a)
  const B = bigrams(b)
  if (A.size === 0 || B.size === 0) return 0
  let shared = 0
  for (const g of A) if (B.has(g)) shared += 1
  return (2 * shared) / (A.size + B.size)
}

/**
 * Damerau-Levenshtein distance (adjacent transposition counted as one edit).
 *
 * Bigram Dice alone is blind to transpositions — "tabel" vs "table" loses
 * half its bigrams and scores BELOW the noise floor, so the term the user
 * actually meant would never be offered. Edit distance rates that pair 0.8,
 * which is the whole point of a feedback loop: surface the near misses and
 * let the user pick.
 */
function editDistance(a: string, b: string): number {
  const s = fold(a).replace(/\s+/g, '')
  const t = fold(b).replace(/\s+/g, '')
  if (s === t) return 0
  if (!s.length) return t.length
  if (!t.length) return s.length
  const rows: number[][] = Array.from({ length: s.length + 1 }, (_, i) =>
    Array.from({ length: t.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  )
  for (let i = 1; i <= s.length; i++) {
    for (let j = 1; j <= t.length; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1
      let best = Math.min(
        rows[i - 1][j] + 1,
        rows[i][j - 1] + 1,
        rows[i - 1][j - 1] + cost,
      )
      if (
        i > 1 &&
        j > 1 &&
        s[i - 1] === t[j - 2] &&
        s[i - 2] === t[j - 1]
      ) {
        best = Math.min(best, rows[i - 2][j - 2] + 1)
      }
      rows[i][j] = best
    }
  }
  return rows[s.length][t.length]
}

function editSimilarity(a: string, b: string): number {
  const longest = Math.max(
    fold(a).replace(/\s+/g, '').length,
    fold(b).replace(/\s+/g, '').length,
  )
  if (longest === 0) return 0
  return Math.max(0, 1 - editDistance(a, b) / longest)
}

export interface TerminologyMatch {
  entry: TerminologyEntry
  /** 0..1, higher is closer. Deterministic. */
  score: number
  /** Which surface produced the score — shown to the user, not inferred. */
  matchedOn: 'id' | 'term' | 'word' | 'description' | 'similar'
  /** The exact defined string that matched. */
  matchedText: string
  /**
   * 0 when the match came from the entry's own id, 1 from a secondary
   * surface (tag / source term). Used only to break score ties: an id near
   * miss is the term itself, a tag near miss is metadata about it. Without
   * this, "tabel" ranked `badge` (tag "label") above `table` purely on
   * alphabetical order and the intended term fell out of view.
   */
  surfaceRank: 0 | 1
}

/**
 * Score one entry against a folded query. Returns the single best surface
 * match. Ordering of the checks encodes precedence: an exact id beats an
 * exact surface term, which beats a word hit, which beats fuzzy similarity.
 */
function scoreEntry(entry: TerminologyEntry, query: string): TerminologyMatch | null {
  const q = fold(query)
  if (!q) return null
  const surfaces: Array<[string, TerminologyMatch['matchedOn']]> = [
    [entry.id, 'id'],
    ...(entry.terms ?? []).map(
      (t) => [t, 'term'] as [string, TerminologyMatch['matchedOn']],
    ),
  ]

  let best: TerminologyMatch | null = null
  const consider = (candidate: TerminologyMatch) => {
    if (!best || candidate.score > best.score) best = candidate
  }

  for (const [surface, matchedOn] of surfaces) {
    const s = fold(surface)
    if (!s) continue
    const surfaceRank: 0 | 1 = matchedOn === 'id' ? 0 : 1
    if (s === q) {
      consider({ entry, score: 1, matchedOn, matchedText: surface, surfaceRank })
      continue
    }
    // Containment either way — "지점 검색" contains the defined term "지점";
    // the defined id "data_table" contains a typed "table".
    if (s.includes(q) || q.includes(s)) {
      const ratio =
        Math.min(s.length, q.length) / Math.max(s.length, q.length)
      consider({
        entry,
        score: 0.6 + 0.3 * ratio,
        matchedOn,
        matchedText: surface,
        surfaceRank,
      })
      continue
    }
    const qWords = new Set(parts(query))
    const sWords = parts(surface)
    if (sWords.some((w) => qWords.has(w))) {
      consider({
        entry,
        score: 0.55,
        matchedOn: 'word',
        matchedText: surface,
        surfaceRank,
      })
      continue
    }
    // Take the better of bigram overlap and edit similarity: the two catch
    // different error shapes (dropped/extra characters vs transpositions),
    // and a near miss must clear the noise floor to be offered at all.
    const similarity = Math.max(
      diceCoefficient(surface, query),
      editSimilarity(surface, query),
    )
    if (similarity > 0) {
      consider({
        entry,
        score: similarity * 0.7,
        matchedOn: 'similar',
        matchedText: surface,
        surfaceRank,
      })
    }
  }

  // A description hit is weak evidence — it can only surface a candidate for
  // the user to look at, never outrank a real surface match.
  if (entry.description) {
    const qWords = parts(query).filter((w) => w.length >= 3)
    const dWords = new Set(parts(entry.description))
    if (qWords.length > 0 && qWords.some((w) => dWords.has(w))) {
      consider({
        entry,
        score: 0.35,
        matchedOn: 'description',
        matchedText: entry.description,
        surfaceRank: 1,
      })
    }
  }

  return best
}

export interface ResolveOptions {
  /** Restrict to certain kinds (e.g. only actions when picking a step). */
  kinds?: readonly TerminologyKind[]
  /** Max candidates returned. Default 8. */
  limit?: number
  /** Score floor. Default 0.3 — below this the match is noise, not a suggestion. */
  minScore?: number
  /**
   * Scope the calling application, if any. The resolution pool is always
   * every `platform`-scope entry, unioned with entries whose `scope` equals
   * this value when it is given. Entries that belong to any OTHER
   * application's scope stay invisible — that is the cross-application
   * vocabulary leak this option closes.
   *
   * Omitting it resolves platform vocabulary only, identical to today.
   */
  applicationScope?: string
}

export interface ResolveResult {
  query: string
  /** True only when the query is exactly one defined term. */
  exact: TerminologyEntry | null
  /** Ranked DEFINED candidates to show the user. Never invented. */
  candidates: TerminologyMatch[]
  /**
   * True when nothing defined resembles the query. This is an honest signal,
   * not a failure: the term is simply not part of coogo's terminology yet, and
   * the answer is to define it (source-anchored), not to guess it.
   */
  undefinedTerm: boolean
}

/**
 * 0 when the entry's scope is the caller's `applicationScope` — the app's
 * own vocabulary is more specific to the user's context — 1 for `platform`
 * (or anything else that made it into the pool). Tie-break only; never
 * affects `score` itself.
 */
function scopeSpecificity(entry: TerminologyEntry, applicationScope?: string): 0 | 1 {
  return applicationScope !== undefined && entry.scope === applicationScope ? 0 : 1
}

/** Platform entries, unioned with `applicationScope` entries when given. */
function selectPool(
  entries: readonly TerminologyEntry[],
  options: ResolveOptions,
): readonly TerminologyEntry[] {
  const kinds = options.kinds
  const applicationScope = options.applicationScope
  return entries.filter((entry) => {
    if (kinds && !kinds.includes(entry.kind)) return false
    if (entry.scope === 'platform') return true
    return applicationScope !== undefined && entry.scope === applicationScope
  })
}

/**
 * Core resolver, parameterised over the entry pool it searches. There are
 * ZERO production consumers of this export today — it exists purely so
 * tests can inject a controlled, fabricated entry pool (e.g. two scopes
 * sharing an id) without adding fictitious vocabulary to the committed
 * index. Production code should use {@link resolveTerminology}, which
 * delegates here with the embedded index; this function adds no behaviour
 * of its own beyond taking the pool as a parameter.
 */
export function resolveTerminologyAgainstEntries(
  entries: readonly TerminologyEntry[],
  query: string,
  options: ResolveOptions = {},
): ResolveResult {
  const limit = Math.max(1, options.limit ?? 8)
  const minScore = options.minScore ?? 0.3
  const applicationScope = options.applicationScope
  const pool = selectPool(entries, options)

  const matches: TerminologyMatch[] = []
  for (const entry of pool) {
    const match = scoreEntry(entry, query)
    if (match && match.score >= minScore) matches.push(match)
  }

  matches.sort(
    (a, b) =>
      b.score - a.score ||
      scopeSpecificity(a.entry, applicationScope) - scopeSpecificity(b.entry, applicationScope) ||
      a.surfaceRank - b.surfaceRank ||
      a.entry.kind.localeCompare(b.entry.kind) ||
      a.entry.id.localeCompare(b.entry.id),
  )

  // Exactness is ID-based, never surface-based: many entries legitimately
  // share a tag (5 element types carry the tag "button"), so an exact tag hit
  // is ambiguous by nature and must stay a candidate the user chooses from.
  // Two kinds can share an id (an element and an action both named "list") —
  // that is ambiguous too, so it also falls through to candidates. Two
  // scopes sharing an id (a platform default an app overrides) are ambiguous
  // the same way.
  const exactIds = matches.filter((m) => m.matchedOn === 'id' && m.score === 1)
  const exact = exactIds.length === 1 ? exactIds[0].entry : null

  return {
    query,
    exact,
    candidates: matches.slice(0, limit),
    undefinedTerm: matches.length === 0,
  }
}

/**
 * Resolve a user phrase against coogo's DEFINED terminology.
 *
 * Deterministic and offline. Returns an exact hit when the phrase IS a defined
 * term, otherwise ranked candidates for the user to choose from, otherwise
 * `undefinedTerm: true`.
 *
 * With no `applicationScope`, resolves against platform vocabulary only —
 * identical to before per-application scoping existed. With one supplied,
 * the pool additionally includes that application's own entries, and those
 * entries rank above platform entries at equal score.
 */
export function resolveTerminology(
  query: string,
  options: ResolveOptions = {},
): ResolveResult {
  return resolveTerminologyAgainstEntries(INDEX.entries, query, options)
}
